<template>
  <div id="quick-task-result" class="@container">
    <!-- teleport target -->
    <QuickTaskContentSuccess v-if="isPostingSuccess || isScheduleSuccess" />
    <SingleQuickTaskResultWrapper
      v-if="contentScheduleParams"
      :title="activeTaskTitle"
      :icon-url="activeTaskIconUrl"
      :task-type="contentScheduleParams.taskType"
      hide-quick-actions
      class="max-w-2xl mx-auto"
    >
      <TaskResultContentScheduler
        v-bind="contentScheduleParams"
        @close="router.back()"
        @done="handleScheduleComplete"
      />
    </SingleQuickTaskResultWrapper>
    <SingleQuickTaskResultWrapper
      :title="activeTaskTitle"
      :icon-url="activeTaskIconUrl"
      :task-type="postInfo.taskType"
      hide-quick-actions
      class="max-w-2xl mx-auto"
      :class="postInfo.isPosting ? '' : 'sr-only'"
    >
      <div id="quick-content-post"></div>
    </SingleQuickTaskResultWrapper>
    <div
      v-show="
        !postInfo.isPosting &&
        !contentScheduleParams &&
        !isPostingSuccess &&
        !isScheduleSuccess
      "
    >
      <div
        class="absolute z-10 @csm:z-[unset] inset-x-0 flex justify-between items-center w-full max-w-2xl left-1/2 -translate-1/2 not-supports-cq:sm:z-[unset]"
        :class="{
          'top-1/2 pt-12': !isTaskUiFullscreen,
          'top-2/5': isTaskUiFullscreen,
          'sr-only': !showSwiperTriggers,
        }"
      >
        <AppButton
          id="quick-task-swiper-prev"
          icon="pi pi-chevron-left"
          variant="secondary"
          class="-ml-4 md:-ml-7 grow-0"
        />
        <AppButton
          id="quick-task-swiper-next"
          icon="pi pi-chevron-right"
          variant="secondary"
          class="-mr-4 md:-mr-7 grow-0"
        />
      </div>
      <div class="max-w-[40rem] mx-auto relative" data-flip-key="task-main">
        <p
          id="quick-task-result-title"
          class="font-semibold pt-4"
          :data-status="
            isLoadingTaskOrContent || newTaskSTatus.isQueued || newTaskSTatus.isRunning
              ? 'loading'
              : 'ready'
          "
        >
          {{
            $t(
              isLoadingTaskOrContent || newTaskSTatus.isQueued || newTaskSTatus.isRunning
                ? "contentAlmostReady"
                : "contentReady"
            )
          }}
        </p>
        <p class="ma-text-secondary mb-2">
          {{ $t("quickTaskActionDescription") }}
        </p>
        <div>
          <ResultContent
            v-if="taskResult"
            :content="contentList || undefined"
            :loading="isLoadingTaskOrContent"
            :task="taskResult"
            :use-quick-task-wrapper="true"
          />
        </div>
      </div>
    </div>

    <OverlayPanel
      id="quick-task-action-overlay"
      ref="overlay"
      :dismissable="false"
      :close-on-escape="false"
      :show-close-icon="false"
    >
      <div
        class="h-[90vh] w-screen"
        :class="
          regenerateContentProps
            ? 'h-[90vh] max-h-[250px] max-w-sm'
            : 'max-h-[max-content] max-w-md'
        "
      >
        <ContentEditImageSection
          v-if="addImageContentId"
          id="quick-task-image-selector"
          @image-selected="updateContent({ imageUrl: $event })"
          @video-selected="updateContent({ videoUrl: $event })"
        />
        <QuickContentRegenerate
          v-if="regenerateContentProps"
          id="quick-task-regenerator"
          v-bind="regenerateContentProps"
          @cancel="cancelContentRegeneration"
          @accept="updateContent({ content: $event })"
        />
        <span class="absolute top-4 right-4 z-10">
          <AppButton
            :id="
              addImageContentId
                ? 'quick-task-attachment-cancel-btn'
                : 'quick-task-regenerate-cancel-btn'
            "
            icon="pi pi-times"
            variant="secondary"
            @click="cancelImageAddition"
          />
        </span>
      </div>
    </OverlayPanel>
  </div>
</template>

<script setup lang="ts">
import ResultContent from "@/components/singleTask/result/ResultContent.vue";
import OverlayPanel from "@/volt/Popover.vue";
import type { QuickTaskActionPayload } from "@/composables/quickTask";
import type { IContentBase } from "@/types/content";
import QuickContentRegenerate from "./QuickContentRegenerate.vue";
import { cloneDeep, set } from "lodash";
import TaskResultContentScheduler from "@/components/content/schedule/TaskResultContentScheduler.vue";
import SingleQuickTaskResultWrapper from "./SingleQuickTaskResultWrapper.vue";
const ContentEditImageSection = defineAsyncComponent(
  () => import("@/components/content/edit/ContentEditImageSection.vue")
);
import QuickTaskContentSuccess from "@/components/quickTask/meta/QuickTaskContentSuccess.vue";
import { useToast } from "vue-toast-notification";
import { useI18n } from "vue3-i18n";
import Bugsnag from "@bugsnag/js";

defineProps<{
  beforeChange?: () => void;
  afterChange?: () => void;
}>();

const {
  editContent,
  taskResult,
  contentList,
  isLoadingTaskOrContent,
  isTaskUiFullscreen,
  postInfo,
  newTaskSTatus,
  isScheduleSuccess,
  isPostingSuccess,
} = useQuickTask();
const overlay = ref<InstanceType<typeof OverlayPanel> | null>(null);
const addImageContentId = ref<string | null>(null);
const regenerateContentProps = ref<{
  contentId: string;
  dataPath: string;
  tone: string;
} | null>(null);
const { isPending, mutate } = useContentUpdateMutation();
const queryClient = useQueryClient();
const route = useRoute();
const router = useRouter();
const $toast = useToast(defaultToastOptions);
const { t } = useI18n();

const showSwiperTriggers = computed(() => {
  if (
    taskResult.value?.taskType ===
      "communications-outreach/email-marketing/email-series" ||
    taskResult.value?.taskType === "social-media/combined/write-all-social"
  )
    return true;
  return (contentList.value?.length || 0) > 1;
});

const contentScheduleParams = computed(() => {
  const query = route.query;
  const scheduledContentId =
    typeof query.scheduledContentId === "string" ? query.scheduledContentId : "";
  const scheduledContentType =
    typeof query.scheduledContentType === "string" ? query.scheduledContentType : "";

  const isQuickTask = query.isQuickTask === "1";

  if (!isQuickTask) return null;

  if (!scheduledContentId || !scheduledContentType) {
    return null;
  }

  // @ts-ignore
  const contentType = getContentTypeByIntegrationId(scheduledContentType) || "";
  const taskType = contentType ? contentTypeTaskKeyMap[contentType] : "";
  return {
    scheduledContentId,
    scheduledContentType,
    taskType,
  };
});

const { title: activeTaskTitle, iconUrl: activeTaskIconUrl } = useSingleTaskConfigDetails(
  () => contentScheduleParams.value?.taskType || postInfo.taskType || ""
);

function editTask(payload: QuickTaskActionPayload) {
  if (payload.type !== "edit") return;
  const { contentId, contentType, dataPath } = payload;
  if (!contentId || !dataPath || !contentType) return;
  editContent({
    contentId,
    contentType,
    dataPath,
  });
}

function cancelImageAddition() {
  addImageContentId.value = null;
  overlay.value?.hide();
}

function cancelContentRegeneration() {
  regenerateContentProps.value = null;
  overlay.value?.hide();
}

function addMedia(payload: QuickTaskActionPayload, event: Event) {
  addImageContentId.value = payload.contentId || null;
  cancelContentRegeneration();
  overlay.value?.show(event, event.target);
}

function handleScheduleComplete() {
  isScheduleSuccess.value = true;
  router.back();
}

function regenerate(payload: QuickTaskActionPayload, event: Event) {
  if (payload.type !== "regenerate") return;
  const { contentId, tone, dataPath } = payload;
  regenerateContentProps.value = {
    contentId,
    dataPath,
    tone,
  };
  cancelImageAddition();
  overlay.value?.show(event, event.target);
}

function handleQuickTaskResultAction(_event: Event) {
  const event = _event as CustomEvent<QuickTaskActionPayload>;
  if (!event.detail?.type || !event.detail?.contentId) return;
  const actionKey = event.detail.type;
  if (!actionKey) return;
  onActionClick(event.detail, _event);
}
function onActionClick(payload: QuickTaskActionPayload, event: Event) {
  // if (props.beforeChange && payload.type === "edit") props.beforeChange();
  switch (payload.type) {
    case "edit":
      editTask(payload);
      break;
    case "add-media":
      addMedia(payload, event);
      break;
    case "regenerate":
      regenerate(payload, event);
      break;
  }
}

async function updateContent(
  update:
    | { imageUrl: string; content?: undefined; videoUrl?: undefined }
    | { content: string; imageUrl?: undefined; videoUrl?: undefined }
    | { videoUrl: string; content?: undefined; imageUrl?: undefined }
) {
  if (isPending.value) return;
  try {
    const { imageUrl, content, videoUrl } = update;
    if ((imageUrl || videoUrl) && !addImageContentId.value) return;
    if (content && !regenerateContentProps.value) return;
    const id = addImageContentId.value || regenerateContentProps.value?.contentId;
    if (!id) return;
    let existingContent: IContentBase | undefined;
    const images: string[] = [];
    const videos: string[] = [];
    existingContent = contentList.value?.find(
      item => item.id === addImageContentId.value
    );
    if (!existingContent) {
      existingContent = await queryClient.ensureQueryData({
        queryKey: ["task-content", id],
        queryFn: async () => await fetchSingleContentById({ contentId: id }),
      });
    }
    const payload: Partial<IContentBase> = {};

    if (imageUrl) {
      images.push(...(existingContent?.images || []), imageUrl);
      payload.images = images;
    }
    if (videoUrl) {
      // Handle videos field which might be string or array
      const existingVideos = existingContent?.videos;
      if (typeof existingVideos === "string" && existingVideos) {
        videos.push(existingVideos, videoUrl);
      } else if (Array.isArray(existingVideos)) {
        videos.push(...existingVideos, videoUrl);
      } else {
        videos.push(videoUrl);
      }
      payload.videos = videos;
    }
    if (content) {
      const dataPath = regenerateContentProps.value?.dataPath;
      if (!dataPath) return;
      const resultData = cloneDeep(existingContent?.resultData) || {};
      set(payload, "resultData", resultData);
      set(payload, dataPath, content);
    }
    overlay.value?.hide();
    mutate(
      {
        id,
        ...payload,
      },
      {
        onSettled() {
          addImageContentId.value = null;
          regenerateContentProps.value = null;
        },
      }
    );
  } catch (error) {
    if (Bugsnag.isStarted()) Bugsnag.notify(error as Error);
    $toast.error(t("errors.tryAgain"));
  }
}

onMounted(() => {
  document.addEventListener(QUICK_TASK_ACTION_EVENT, handleQuickTaskResultAction);
});

onUnmounted(() => {
  document.removeEventListener(QUICK_TASK_ACTION_EVENT, handleQuickTaskResultAction);
});
</script>

<style>
#quick-task-result .swiper-pagination {
  --swiper-pagination-bottom: auto;
  --swiper-pagination-top: -4px;
}

#quick-task-result .swiper-button-disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
